import './Global.scss';
import NavigationMenu from 'extensions/navigation-menu';
import ContinueShopping from 'extensions/continue-shopping';
import { PageManager } from '@mvps-genesis/bootstrap';
import { GlobalMessageContainer } from 'extensions/messages';
import MiniBasket from 'extensions/mini-basket';
import { initCarousels } from 'extensions/carousel';
import dynamicFormElementAttributes from 'extensions/dynamic-form-element-attributes';
import WebFont from 'webfontloader';
import ConfirmAction from 'extensions/confirm-action';
import { initRecaptchaForms } from 'extensions/recaptcha-v3';
import { PhoneFields } from 'extensions/phone-fields';
import { SkipToSection, MenuBar } from '@mvps-genesis/accessibility';

export default class Global extends PageManager {

	// eslint-disable-next-line max-lines-per-function
	constructor(pageContext) {

		super(pageContext);

		this.initSkipToSection();

		WebFont.load({
			typekit: {
				id: 'goc8whv'
			}
		});

		this.initSearchPreview();

		/**
		 * Create global message container
		 */
		new GlobalMessageContainer({ container: '#js-messages' });

		/**
		 * Initialize carousels
		 */
		initCarousels();

		this.initializeHTMLFormValidator();

		/**
		 * Attach reCAPTCHA v3 token generation to forms opted in via
		 * `data-recaptcha-action` (e.g. the create-account form).
		 */
		initRecaptchaForms();

		/**
		 * Initialize the Dynamic Form-Element Attributes
		 */
		dynamicFormElementAttributes();

		this.initModals();
		this.initLazyload();
		this.initFacets();
		this.initCookieConsent();
		this.initAddressEvents();
		this.initAddressValidation();
		this.initPhoneFields();
		this.initScrollTo();
		this.initBackToTop();
		this.initStickyListener();
		this.initAjaxLoginRegister();
		this.initFormAjaxLoaders();
		this.initTooltip();
		this.initTabbedCarousels();
		this.initGTM();
		this.promoRotate();
	}

	// eslint-disable-next-line max-lines-per-function
	onReady() {

		/**
		 * Initialize the Navigation Menu extension
		 */
		const mainMenuElem = document.querySelector('.js-navigation-menu');
		if (mainMenuElem) {
			new NavigationMenu({
				drawers: '.js-navigation-menu__drawer',
				mainMenu: mainMenuElem,
				mainMenuCloseButtons: '.js-navigation-menu__close',
				mainMenuOpenButtons: '.js-navigation-menu__open',
				menuItems: '.js-navigation-menu__link',
				parentMenus: '.js-navigation-menu__has-child',
				showPreviousMenuButtons: '.js-navigation-menu__previous',
			});
		}

		const accountLinks = document.getElementById('js-customer-account-links');
		if (accountLinks) {
			// Basic instance constructor
			new MenuBar({
				breakpoint: false,
				element: accountLinks,
				navigateOnDown: true,
				navigateOnEnter: true,
				navigateOnSpace: true
			});
		}

		const staticLinks = document.getElementById('js-static-page-links');
		if (staticLinks) {
			// Basic instance constructor
			new MenuBar({
				element: staticLinks,
				navigateOnDown: true,
				navigateOnEnter: true,
				navigateOnSpace: true
			});
		}

		/**
		 * Initialize the Mini-Basket extension
		 */
		new MiniBasket({
			openOnAddToCart: true
		});

		/**
		 * Initialize continue shopping link
		 */
		new ContinueShopping();
		this.searchFormEvents();
		this.triggerPrint();
		this.initCollapsibleMenu();

		new ConfirmAction();

	}

	initSkipToSection() {
		const sections = [];

		const menuElem = document.querySelector('.js-navigation-menu');

		if (menuElem) {
			sections.push({
				label: 'Menu',
				targetElement: menuElem
			});
		}

		const searchElem = document.querySelector('[itemprop="query-input"]');

		if (searchElem) {
			sections.push({
				label: 'Search',
				targetElement: searchElem
			});
		}

		const facetsElem = document.getElementById('js-facets');

		if (facetsElem) {
			sections.push({
				label: 'Filters',
				targetElement: facetsElem
			});
		}

		sections.push({
			label: 'Content',
			targetElement: '.g-main-content-element'
		});

		sections.push({
			label: 'Footer',
			targetElement: '.g-site-footer'
		});

		SkipToSection.createFromArray(sections);
	}

	async initSearchPreview() {
		await import('extensions/search-preview');
	}

	async initLazyload() {
		// Elements
		const lazyLoadElements = document.getElementsByClassName('lazyload');

		if (lazyLoadElements.length === 0) {
			return;
		}

		// Module
		await import('lazysizes/plugins/unveilhooks/ls.unveilhooks');
		await import('lazysizes/plugins/bgset/ls.bgset');
		await import('lazysizes/plugins/custommedia/ls.custommedia');
		await import('lazysizes');
	}

	async initFacets() {
		const facetsContainerElement = document.getElementById('js-facets');

		if (!facetsContainerElement) {
			return;
		}

		const { default: Facets } = await import('extensions/facets/Facets');

		new Facets(facetsContainerElement);
	}

	async initCookieConsent() {
		const cookieName = `mm5-${this.pageContext.Store_Code}-genesis-cookie-consent`;

		if (document.cookie.indexOf(cookieName) !== -1) {
			return;
		}

		const { default: CookieConsent } = await import('extensions/cookie-consent/CookieConsent');

		let contentLink = 'https://www.cookiesandyou.com/';
		let contentLinkTarget = '_blank';

		if (this.pageContext.Privacy_Policy_Url) {
			contentLink = this.pageContext.Privacy_Policy_Url;
			contentLinkTarget = '';
		}

		new CookieConsent({
			content: {
				dismiss: 'Got it!',
				href: contentLink,
				link: 'Learn More',
				message: 'This website uses cookies to ensure you get the best experience on our website.',
				target: contentLinkTarget
			},
			cookie: {
				name: cookieName,
				secure: true
			},
			palette: {
				button: {
					background: '#F9F9F9',
					text: '#000000'
				},
				popup: {
					background: '#222222',
					text: '#FFFFFF'
				}
			}
		});
	}

	async initializeHTMLFormValidator() {
		const forms = document.getElementsByClassName('js-form-validator');

		if (forms.length === 0) {
			return;
		}

		const { HTMLFormValidator } = await import('extensions/form-validation');

		for (const form of forms) {
			form.HTMLFormValidator = new HTMLFormValidator({
				fieldContainer: '.g-form-list__item',
				formElement: form
			});
		}
	}

	async initModals() {
		const hasModalTrigger = document.querySelector(`[data-micromodal-trigger]`);

		if (!hasModalTrigger) {
			return;
		}

		const { default: Modal } = await import('extensions/modal');
		const modal = new Modal();

		/**
		 * Initialize modals:
		 * 	Pass list of modal ids without an overlay that should close when
		 * 	clicking off of modal.
		 */
		modal.init([
			'js-search-form',
			'js-global-account'
		]);
	}

	async initAddressValidation() {
		const addressValidationElement = document.getElementById('address-validation');

		if (!addressValidationElement) {
			return;
		}

		const { default: AddressValidation } = await import('extensions/address-validation/AddressValidation');

		new AddressValidation(addressValidationElement);
	}

	async initAddressEvents() {
		const datalists = document.querySelectorAll('[data-address-datalist]');
		const toggles = document.querySelectorAll('[data-address-toggle]');

		if (datalists.length === 0 && toggles.length === 0) {
			return;
		}

		const { AddressEvents } = await import('extensions/address-events');

		new AddressEvents(datalists, toggles);
	}

	/**
	 * Loaded statically (not via dynamic import) on purpose: the visible
	 * phone/ext inputs are unnamed and only reach the submitted hidden field
	 * through the submit listener PhoneFields installs, so that listener has
	 * to exist before the form can possibly be submitted.
	 */
	initPhoneFields() {
		const phoneFieldGroups = document.querySelectorAll('[data-phone-fields]');

		if (phoneFieldGroups.length === 0) {
			return;
		}

		new PhoneFields(phoneFieldGroups);
	}

	searchFormEvents() {
		const searchForm = document.getElementById('js-search-form');
		const searchInput = document.getElementById('js-search-form-input');

		if (!searchForm || !searchInput) {
			return;
		}

		const searchClearButton = document.getElementById('js-site-header__search-clear');

		if (searchClearButton) {
			const clearButtonToggleClass = 'is-visible';

			searchClearButton.addEventListener('click', () => {
				searchInput.value = '';
				searchClearButton.classList.remove(clearButtonToggleClass);
				searchClearButton.setAttribute('aria-hidden', 'true');
			});

			searchInput.addEventListener('input', () => {
				searchClearButton.classList.toggle(clearButtonToggleClass, searchInput.value);
				if (searchClearButton.classList.contains(clearButtonToggleClass)) {
					searchClearButton.setAttribute('aria-hidden', 'false');
				} else {
					searchClearButton.setAttribute('aria-hidden', 'true');
				}
			});
		}
	}

	triggerPrint() {

		/**
		 *  Launch Printer Dialog
		 */
		const printTriggers = document.getElementsByClassName('js-trigger-print');

		if (printTriggers.length) {
			for (const element of printTriggers) {
				element.addEventListener('click', function (e) {
					e.preventDefault();
					window.print();
				}, false);
			}
		}

	}

	async initScrollTo() {
		const triggerElements = document.getElementsByClassName('js-scroll-to');

		if (!triggerElements.length) {
			return;
		}

		const { default: ScrollTo } = await import('extensions/scroll-to');

		new ScrollTo(triggerElements, {
			scrollIntoViewOptions: {
				behavior: 'smooth',
				block: 'end',
				inline: 'nearest'
			}
		});
	}

	async initBackToTop() {

		const backToTopButton = document.getElementById('js-back-to-top-button');

		if (!backToTopButton) {
			return;
		}

		const backToTopClass = 'g-back-to-top-button--shown';

		const { default: ScrollTo } = await import('extensions/scroll-to');

		new ScrollTo('#js-back-to-top-button', {
			scrollIntoViewOptions: {
				behavior: 'smooth',
				inline: 'nearest'
			}
		});

		let lastScrollTop = 0;

		window.addEventListener('scroll', () => {
			this.showHideBackToTop(backToTopButton, backToTopClass);

			// Detect if user is scrolling up or down
			let st = window.pageYOffset || document.documentElement.scrollTop;
			if (st > lastScrollTop) {
				document.body.classList.remove('g-user-scrolling-up');
			} else {
				document.body.classList.add('g-user-scrolling-up');
			}

			lastScrollTop = st <= 0 ? 0 : st;
		});

		this.showHideBackToTop(backToTopButton, backToTopClass);

	}

	showHideBackToTop(backToTopButton, backToTopClass) {

		const heightToShowButton = window.innerHeight;
		const offset = heightToShowButton / 2;
		const windowOffset = offset + (isNaN(window.pageYOffset) ? 0 : window.pageYOffset);

		if (windowOffset >= heightToShowButton) {
			backToTopButton.classList.add(backToTopClass);
		} else {
			backToTopButton.classList.remove(backToTopClass);
		}

	}

	initStickyListener() {
		if (!('IntersectionObserver' in window)) {
			return;
		}

		const header = document.querySelector('.g-site-header:not(.g-site-header__unsticky)');

		if (!header) {
			return;
		}

		const observer = new IntersectionObserver(
			([e]) => e.target.classList.toggle('g-site-header__sticky', e.intersectionRatio < 1),
			{ threshold: [1] }
		);

		observer.observe(header);
	}

	async initAjaxLoginRegister() {
		const loginRegisterElement = document.getElementById('js-login-register');
		const globalAccountElement = document.getElementById('js-global-account');
		const globalAccountHasForm = globalAccountElement?.querySelector('.js-login-register-form');

		if (!loginRegisterElement && !globalAccountHasForm) {
			return;
		}

		const { AjaxLoginRegister } = await import('extensions/ajax-login-register');

		if (loginRegisterElement) {
			new AjaxLoginRegister(loginRegisterElement);
		}

		if (globalAccountHasForm) {
			new AjaxLoginRegister(globalAccountElement, { toggleFormEnabled: false });
		}
	}

	async initFormAjaxLoaders() {
		const formElements = document.getElementsByClassName('js-form-show-loading');

		if (!formElements.length) {
			return;
		}

		const { default: AjaxLoader } = await import('extensions/ajax-loader');
		const loadingContainerSelector = '.js-loading-container';
		const loadingElementSelector = '.js-loading-element';

		for (const formElement of formElements) {
			const loadingContainerElement = formElement.closest(loadingContainerSelector);

			if (!loadingContainerElement) {
				continue;
			}

			const loadingElement = loadingContainerElement.querySelector(loadingElementSelector);

			if (!loadingElement) {
				continue;
			}

			formElement.AjaxLoader = new AjaxLoader(loadingElement, {
				overlayElement: loadingContainerElement.hasAttribute('data-disable-loading-overlay') ? false : loadingContainerElement
			});

			formElement.addEventListener('submit', () => {
				formElement.AjaxLoader.show();
			});
		}
	}

	async initTooltip() {

		const tooltipElements = document.getElementsByClassName('js-tooltip');

		if (!tooltipElements.length) {
			return;
		}

		const { default: Tooltip } = await import('extensions/tooltip');

		for (const element of tooltipElements) {
			new Tooltip(element);
		}
	}

	async initCollapsibleMenu() {

		const menuElements = document.getElementsByClassName('js-collapsible-menu');

		if (menuElements.length === 0) {
			return;
		}

		const { default: CollapsibleMenu } = await import('extensions/collapsible-menu');

		new CollapsibleMenu(menuElements);

	}

	async initTabbedCarousels() {

		const tabbedCarouselElements = document.getElementsByClassName('js-tabbed-carousel');

		if (!tabbedCarouselElements.length) {
			return;
		}

		const { default: TabbedCarousels } = await import('extensions/tabbed-carousels');

		new TabbedCarousels(tabbedCarouselElements);

	}

	async initGTM() {

		const { default: GTM } = await import('extensions/gtm');

		const gtm = new GTM(this.pageContext);
		gtm.init();

		// Listen for Print Events
		window.addEventListener('beforeprint', () => {
			gtm.sendEvent({
				action: 'Click',
				category: 'Print',
				label: document.title,
				noninteractionHit: false
			});
		});
	}

	promoRotate() {
		document.addEventListener('DOMContentLoaded', () => {
			const container = document.getElementById('header_message');
			if (!container) return;

			let items = Array.from(container.children);
			let itemCount = items.length;
			if (itemCount < 2) return;

			const duration = 500;
			const pause = 2500;

			let current = 0;
			let isTransitioning = false;
			let intervalId = null;
			let isPaused = false;
			let heights = [];

			const pauseBtn = document.getElementById('pause-btn');
			const playBtn = document.getElementById('play-btn');

			// Helper to get cumulative height up to index
			function getTranslateY(index) {
				return heights.slice(0, index).reduce((sum, h) => sum + h, 0);
			}

			// Measure and store each item's height
			function measureHeights() {
				items = Array.from(container.children);
				heights = items.map(item => item.offsetHeight);
			}

			// Clone the first item and append to end for seamless looping
			function setupClone() {
				const existingClone = container.querySelector('.promo-clone');
				if (existingClone) container.removeChild(existingClone);
				const firstClone = items[0].cloneNode(true);
				firstClone.classList.add('promo-clone');
				container.appendChild(firstClone);
				items = Array.from(container.children);
				itemCount = items.length - 1; // exclude clone for logic
			}

			function nextSlide() {
				if (isTransitioning) return;
				isTransitioning = true;
				current++;
				const maxHeight = items[0].offsetHeight; // All messages have the same height now
				container.style.transition = `transform ${duration}ms cubic-bezier(0.24, -0.17, 0.31, 0.98)`;
				container.style.transform = `translateY(-${current * maxHeight}px)`;

				setTimeout(() => {
					if (current === itemCount) {
						container.style.transition = 'none';
						container.style.transform = 'translateY(0)';
						current = 0;
					}
					isTransitioning = false;
				}, duration);
			}

			function startRotation() {
				if (intervalId) clearInterval(intervalId);
				intervalId = setInterval(nextSlide, pause + duration);
			}

			function stopRotation() {
				if (intervalId) clearInterval(intervalId);
				intervalId = null;
			}

			// Initial setup after DOM is ready and fonts are loaded
			function initialize() {
				setupClone();
				measureHeights();
				setWrapperHeightToMax();
				setAllMessageHeightsToMax();
				container.style.transition = 'none';
				container.style.transform = 'translateY(0)';
				current = 0;
				isTransitioning = false;
				isPaused = false;
				playBtn.classList.add('u-hidden');
				pauseBtn.classList.remove('u-hidden');
				startRotation();
			}

			// Re-measure and reset on resize
			window.addEventListener('resize', () => {
				stopRotation();
				initialize();
			});

			// Play by default
			initialize();

			// Pause button
			pauseBtn.addEventListener('click', function () {
				stopRotation();
				isPaused = true;
				pauseBtn.classList.add('u-hidden');
				playBtn.classList.remove('u-hidden');
			});

			// Play button
			playBtn.addEventListener('click', function () {
				if (!isPaused) return;
				startRotation();
				isPaused = false;
				playBtn.classList.add('u-hidden');
				pauseBtn.classList.remove('u-hidden');
			});

			function setWrapperHeightToMax() {
				const wrapper = document.querySelector('.header-message-wrapper');
				const container = document.getElementById('header_message');
				if (!wrapper || !container) return;
				const messages = container.querySelectorAll('.g-site-header__message');
				let maxHeight = 0;
				messages.forEach(msg => {
					const h = msg.offsetHeight;
					if (h > maxHeight) maxHeight = h;
				});
				wrapper.style.height = maxHeight + 'px';
			}
			function setAllMessageHeightsToMax() {
				const messages = container.querySelectorAll('.g-site-header__message');
				let maxHeight = 0;
				messages.forEach(msg => {
					msg.style.height = ''; // Reset any previous height
					const h = msg.offsetHeight;
					if (h > maxHeight) maxHeight = h;
				});
				messages.forEach(msg => {
					msg.style.height = maxHeight + 'px';
				});
			}
		});
	}

}
